import {
  DEFAULT_SETTINGS,
  newId,
  parseSettings,
  type CalendarKind,
  type Locale,
  type LoginResponse,
  type Role,
  type TenantSettings,
} from '@dairy/shared';
import { db, getMeta, setMeta, wipeFarmData } from '@/db/db';
import { setRepoUser } from '@/db/repo';
import { API_BASE, ApiError, apiJson, setApiAuth } from '@/lib/api';

/** PL-3 session profile kept on the device so the app opens offline. */
export interface SessionProfile {
  userId: string;
  name: string;
  phone: string;
  role: Role;
  tenantId: string;
  tenantName: string;
  settings: TenantSettings;
  locale: Locale;
  primaryCalendar: CalendarKind;
  pendingPreferences?: { locale?: Locale; primaryCalendar?: CalendarKind };
  lastOnlineAuthAt: string;
}

export interface SessionState {
  status: 'loading' | 'signedOut' | 'signedIn';
  profile: SessionProfile | null;
  /** True when we are signed in from the device profile without a server session yet. */
  offlineSession: boolean;
  /** The server refused our session while online: the user must log in to sync (data stays on the device). */
  needsLogin: boolean;
  partnerRecoveryRequired: boolean;
}

export const OFFLINE_SESSION_DAYS = 30;
const SESSION_KEY = 'session';

let state: SessionState = { status: 'loading', profile: null, offlineSession: false, needsLogin: false, partnerRecoveryRequired: false };
let accessToken: string | null = null; // memory only (PL-3)
const listeners = new Set<() => void>();

function setState(patch: Partial<SessionState>): void {
  state = { ...state, ...patch };
  setRepoUser(state.profile?.userId ?? null);
  for (const l of listeners) l();
}

export const sessionStore = {
  get: () => state,
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export async function deviceId(): Promise<string> {
  let id = await getMeta<string>('deviceId');
  if (!id) {
    id = newId();
    await setMeta('deviceId', id);
  }
  return id;
}

function tokenExpiresInMs(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return payload.exp ? payload.exp * 1000 - Date.now() : Infinity;
  } catch {
    return 0;
  }
}

function profileFrom(r: LoginResponse): SessionProfile {
  return {
    userId: r.user.id,
    name: r.user.name,
    phone: r.user.phone,
    role: r.user.role,
    tenantId: r.tenant.id,
    tenantName: r.tenant.name,
    settings: parseSettings(r.tenant.settings),
    locale: r.user.locale,
    primaryCalendar: r.user.primaryCalendar,
    lastOnlineAuthAt: new Date().toISOString(),
  };
}

async function storeProfile(p: SessionProfile): Promise<void> {
  await setMeta(SESSION_KEY, p);
}

const PARTNER_LOCK = 'partnerPrivacyLock';
const privateEntities = new Set(['partner', 'partnerContribution', 'shareGrant', 'distribution', 'projectionAssumptionSet', 'projectionScenario']);

/** Keep private edits intact when a former owner authenticates as a manager. */
async function prepareRole(body: LoginResponse, previous: SessionProfile | undefined): Promise<void> {
  const lock = await getMeta<{ tenantId: string }>(PARTNER_LOCK);
  if (lock && (body.user.role !== 'OWNER' || body.tenant.id !== lock.tenantId)) {
    throw new PartnerRecoveryRequiredError();
  }
  if (body.user.role === 'MANAGER') {
    // Stop access before checking IndexedDB, including while an old pull is in flight.
    accessToken = null;
    // Suspend protected routes without navigating to login during a successful manager refresh.
    setState({ status: 'loading', profile: null, offlineSession: false, needsLogin: false });
    const privateTables = [db.partners, db.partnerContributions, db.shareGrants, db.distributions, db.projectionAssumptionSets, db.projectionScenarios];
    const blocked = await db.transaction('rw', [db.outbox, db.meta, ...privateTables], async () => {
      const pending = await db.outbox.filter((entry) => privateEntities.has(entry.entity)).count();
      if (pending) {
        await db.meta.put({ key: PARTNER_LOCK, value: { tenantId: previous?.tenantId ?? body.tenant.id } });
        return true;
      }
      const hasPrivateData = (await Promise.all(privateTables.map((table) => table.count()))).some(Boolean);
      if (hasPrivateData || previous?.role === 'OWNER') {
        await Promise.all(privateTables.map((table) => table.clear()));
        await db.meta.put({ key: 'cursor', value: 0 });
      }
      await db.meta.delete('projectionDefaults');
      return false;
    }).catch((error: unknown) => {
      setState({ status: 'signedOut', profile: null });
      throw error;
    });
    if (blocked) {
      setState({ status: 'signedOut', partnerRecoveryRequired: true });
      throw new PartnerRecoveryRequiredError();
    }
  } else if (lock || previous?.role === 'MANAGER') {
    await db.meta.put({ key: 'cursor', value: 0 });
    await db.meta.delete(PARTNER_LOCK);
  }
}

let refreshing: Promise<boolean> | null = null;

/** Single-flight refresh of the access token with the httpOnly cookie. */
export function refresh(): Promise<boolean> {
  if (state.needsLogin) return Promise.resolve(false);
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (res.status === 401) {
        accessToken = null;
        if (state.profile) setState({ needsLogin: true });
        return false;
      }
      if (!res.ok) return false;
      const body = (await res.json()) as LoginResponse;
      if (state.profile && state.profile.userId !== body.user.id) {
        // Another user's cookie: never mix farms on one device.
        accessToken = null;
        return false;
      }
      const previous = (await getMeta<SessionProfile>(SESSION_KEY)) ?? undefined;
      await prepareRole(body, previous);
      accessToken = body.accessToken;
      const pending = state.profile?.pendingPreferences;
      const p = { ...profileFrom(body), ...pending, pendingPreferences: pending };
      await storeProfile(p);
      setState({ status: 'signedIn', profile: p, offlineSession: false, needsLogin: false, partnerRecoveryRequired: false });
      void syncProfilePreferences();
      return true;
    } catch {
      return false; // offline
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function getToken(): Promise<string | null> {
  if (accessToken && tokenExpiresInMs(accessToken) < 60_000 && navigator.onLine) await refresh();
  return accessToken;
}

setApiAuth({ getToken, refresh, deviceId, preferences: () => state.profile });

/** App start (S1-T11): online → refresh; offline → the device profile if the last online login is within 30 days. */
export async function bootstrapSession(): Promise<void> {
  if (await getMeta(PARTNER_LOCK)) {
    accessToken = null;
    setState({ status: 'signedOut', profile: null, offlineSession: false, needsLogin: false, partnerRecoveryRequired: true });
    return;
  }
  const profile = (await getMeta<SessionProfile>(SESSION_KEY)) ?? null;
  if (profile) setState({ profile: { ...profile, settings: parseSettings(profile.settings) } });
  if (navigator.onLine) {
    const ok = await refresh();
    if (ok) return;
    if (state.partnerRecoveryRequired) return;
  }
  if (profile && Date.now() - Date.parse(profile.lastOnlineAuthAt) < OFFLINE_SESSION_DAYS * 86_400_000) {
    setState({ status: 'signedIn', profile: { ...profile, settings: parseSettings(profile.settings) }, offlineSession: true });
    return;
  }
  setState({ status: 'signedOut', profile: null });
}

export class OtherUserDataError extends Error {
  constructor(readonly pending: number) {
    super('Another user has unsynced changes on this device');
  }
}

export class PartnerRecoveryRequiredError extends Error {
  constructor() { super('An owner must sign in and sync owner-only changes first'); }
}

export async function login(identifier: string, secret: string): Promise<SessionProfile> {
  const body = await apiJson<LoginResponse>('/auth/login', {
    method: 'POST',
    json: { identifier, secret, deviceId: await deviceId() },
  });
  const previous = await getMeta<SessionProfile>(SESSION_KEY);
  const lock = await getMeta<{ tenantId: string }>(PARTNER_LOCK);
  try {
    await prepareRole(body, previous);
  } catch (error) {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include', headers: { Authorization: `Bearer ${body.accessToken}` } }).catch(() => undefined);
    throw error;
  }
  if (previous && (previous.userId !== body.user.id || previous.tenantId !== body.tenant.id)) {
    const pending = await db.outbox.count();
    const recovering = lock && body.user.role === 'OWNER' && body.tenant.id === lock.tenantId;
    if (pending > 0 && !recovering) {
      // Never drop someone else's unsynced work silently: they must log in and sync first.
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include', headers: { Authorization: `Bearer ${body.accessToken}` } }).catch(() => undefined);
      throw new OtherUserDataError(pending);
    }
    if (!recovering) await wipeFarmData();
  }
  accessToken = body.accessToken;
  const pending = previous?.userId === body.user.id ? previous.pendingPreferences : undefined;
  const p = { ...profileFrom(body), ...pending, pendingPreferences: pending };
  await storeProfile(p);
  setState({ status: 'signedIn', profile: p, offlineSession: false, needsLogin: false, partnerRecoveryRequired: false });
  void syncProfilePreferences();
  try {
    await navigator.storage?.persist?.(); // PL-8: keep offline data on Android
  } catch {
    /* not supported */
  }
  return p;
}

/** PL-3 logout. The UI blocks it while changes are unsynced (or the user typed N to discard them). */
export async function logout(): Promise<void> {
  try {
    if (navigator.onLine && accessToken) {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include', headers: { Authorization: `Bearer ${accessToken}` } });
    }
  } catch {
    /* offline: the server session expires on its own */
  }
  accessToken = null;
  await wipeFarmData();
  setState({ status: 'signedOut', profile: null, offlineSession: false, needsLogin: false, partnerRecoveryRequired: false });
}

/** Settings and tenant name arrive with every pull (PL-6). */
export async function updateProfile(patch: Partial<SessionProfile>): Promise<void> {
  if (!state.profile) return;
  const p = { ...state.profile, ...patch };
  await storeProfile(p);
  setState({ profile: p });
}

export function currentSettings(): TenantSettings {
  return state.profile?.settings ?? DEFAULT_SETTINGS;
}

/** Local-first preferences are persisted with the session until PATCH /me succeeds. */
export async function setUserPreferences(patch: { locale?: Locale; primaryCalendar?: CalendarKind }): Promise<void> {
  await updateProfile({ ...patch, pendingPreferences: { ...state.profile?.pendingPreferences, ...patch } });
  void syncProfilePreferences();
}

let syncingPreferences: Promise<void> | null = null;
export function syncProfilePreferences(): Promise<void> {
  syncingPreferences ??= (async () => {
    while (navigator.onLine && accessToken && state.profile?.pendingPreferences) {
      const userId = state.profile.userId;
      const pending = state.profile.pendingPreferences;
      try {
        await apiJson('/me', { method: 'PATCH', json: pending });
      } catch {
        return; // retry on reconnection or the next session refresh
      }
      if (state.profile?.userId !== userId) return;
      if (state.profile.pendingPreferences === pending) await updateProfile({ pendingPreferences: undefined });
    }
  })().finally(() => { syncingPreferences = null; });
  return syncingPreferences;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

export function isApiError(e: unknown, code?: string): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.code === code);
}
